import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import "../styles.css";
import interLatinWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import playfair600Woff2 from "@fontsource/playfair-display/files/playfair-display-latin-600-normal.woff2?url";
import heroLcpMobile from "@/assets/home/246-m.webp";
import heroLcpDesktop from "@/assets/gallery/246.webp";
import { BookingDialogLoader } from "@/components/site/BookingDialogLoader";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "RKD Tile — Custom Tile Craftsmanship in Utah" },
      { name: "description", content: "Custom residential & commercial tile installation across Park City, Salt Lake & Utah County. 30 years of masterpiece-level workmanship." },
      { name: "author", content: "RKD Tile" },
      { name: "google-site-verification", content: "YL6iDWOgMB7TkZybnLWnehuAoQmLApdtzkXvjcP0SHo" },
      { name: "google-site-verification", content: "LXuvZf2LpUwTs1wypXEPG9Ko0GpYejzrv7ZAGKrUf8M" },
      { property: "og:title", content: "RKD Tile — Custom Tile Craftsmanship in Utah" },
      { property: "og:description", content: "Custom residential & commercial tile installation across Park City, Salt Lake & Utah County. 30 years of masterpiece-level workmanship." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:site", content: "@Lovable" },
      { name: "twitter:title", content: "RKD Tile — Custom Tile Craftsmanship in Utah" },
      { name: "twitter:description", content: "Custom residential & commercial tile installation across Park City, Salt Lake & Utah County. 30 years of masterpiece-level workmanship." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/12d782cb-d539-4f7d-8060-34692cb9f147/id-preview-93553173--949f3e99-ba7f-4b3c-baf2-8e3271d4bbc6.lovable.app-1778698062739.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/12d782cb-d539-4f7d-8060-34692cb9f147/id-preview-93553173--949f3e99-ba7f-4b3c-baf2-8e3271d4bbc6.lovable.app-1778698062739.png" },
    ],
    links: [
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: playfair600Woff2,
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: interLatinWoff2,
        crossOrigin: "anonymous",
      },
    ],
    scripts: [
      {
        children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','G-C1F1K7LJ1Q');function __loadGA(){if(window.__gaLoaded)return;window.__gaLoaded=true;var s=document.createElement('script');s.async=true;s.src='https://www.googletagmanager.com/gtag/js?id=G-C1F1K7LJ1Q';document.head.appendChild(s);}var __gaEvents=['scroll','mousemove','touchstart','keydown','click'];function __gaTrigger(){__gaEvents.forEach(function(e){window.removeEventListener(e,__gaTrigger,{passive:true});});__loadGA();}__gaEvents.forEach(function(e){window.addEventListener(e,__gaTrigger,{passive:true,once:true});});setTimeout(__loadGA,8000);`,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        {/* Before scripts, fonts, and the inlined stylesheet, so the hero request is not queued. */}
        <link
          rel="preload"
          as="image"
          href={heroLcpMobile}
          type="image/webp"
          media="(max-width: 767px)"
          {...{ fetchpriority: "high" }}
        />
        <link
          rel="preload"
          as="image"
          href={heroLcpDesktop}
          type="image/webp"
          media="(min-width: 768px)"
          {...{ fetchpriority: "high" }}
        />
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return (
    <>
      <Outlet />
      <BookingDialogLoader />
    </>
  );
}
